import type {UseFormReturn} from 'react-hook-form';
import {toast} from '@common-web/hooks/use-toast';
import {reportError} from '@common-web/lib/errorReporting';

// Top-level keys the server's per-step final-schema validation produces
// (rfcRouter.ts). An issue whose path starts with one of these maps onto a
// wizard form field once the section prefix (section125Form / mecForm) is
// prepended.
const STEP_KEYS = new Set(['groupInfo', 'admin', 'contacts', 'payrollInfo', 'agentOfRecord']);

type RfcIssue = {path?: (string | number)[]; message?: string};

// Structural keys in the tRPC input / step payloads that carry no meaning for
// the reader — stripped before deriving a field label.
const PATH_NOISE = new Set([
  'formData',
  'section125FormData',
  'mecFormData',
  'clientPortalAccessFormData',
]);

function extractIssues(error: unknown): RfcIssue[] {
  const data = (error as {data?: {issues?: unknown; zodError?: {issues?: unknown}}})?.data;
  const issues = data?.issues ?? data?.zodError?.issues;
  return Array.isArray(issues) ? (issues as RfcIssue[]) : [];
}

function humanize(segment: string | number): string {
  if (typeof segment === 'number') return `#${segment + 1}`;
  return segment.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());
}

// Prefix the message with a readable field label from the issue path. Empty or
// noise-only paths fall back to the raw message; a message that already names
// the field (the final-schema messages do) is left untouched.
function issueLabel(issue: RfcIssue): string {
  const message = issue.message?.trim() || 'Invalid value';
  const path = (issue.path ?? []).filter((p) => !(typeof p === 'string' && PATH_NOISE.has(p)));
  if (path.length === 0) return message;
  const field = humanize(path[path.length - 1]);
  return message.toLowerCase().includes(field.toLowerCase()) ? message : `${field}: ${message}`;
}

/** Toast every failing field's message. Falls back to the generic handler when
 * the error carries no structured zod issues. */
export function surfaceRfcFormErrors(
  error: unknown,
  fallbackTitle: string = 'Form Validation Error',
): void {
  const messages = Array.from(new Set(extractIssues(error).map(issueLabel)));

  if (messages.length === 0) {
    reportError(error, {title: fallbackTitle});
    return;
  }

  reportError(error, {silent: true, context: 'RfcForm.validation'});
  toast({
    title: 'Please fix the following before saving',
    variant: 'destructive',
    description: (
      <ul className='list-disc space-y-1 pl-4'>
        {messages.map((message, index) => (
          <li key={index}>{message}</li>
        ))}
      </ul>
    ),
  });
}

// tRPC input-validation paths lead with the input shape; map their form-data
// root onto the corresponding react-hook-form root key.
const INPUT_ROOT_MAP: Record<string, string> = {
  section125FormData: 'section125Form',
  mecFormData: 'mecForm',
  clientPortalAccessFormData: 'clientPortalAccessForm',
};

// Resolve a zod issue path to its react-hook-form field name. Handles both the
// per-step final-schema paths (['groupInfo', ...], needing the section prefix)
// and the tRPC input-validation paths (['formData', 'section125FormData', ...]).
function toRhfFieldName(
  path: (string | number)[],
  fieldPrefix: 'section125Form' | 'mecForm' | null,
): string | null {
  if (path[0] === 'formData' && typeof path[1] === 'string' && INPUT_ROOT_MAP[path[1]]) {
    return [INPUT_ROOT_MAP[path[1]], ...path.slice(2)].join('.');
  }
  if (fieldPrefix && path.length > 0 && STEP_KEYS.has(String(path[0]))) {
    return [fieldPrefix, ...path].join('.');
  }
  return null;
}

/** Highlight the offending fields inline by mapping each zod issue path onto its
 * react-hook-form field name. No-op for paths that don't resolve to a field. */
export function applyRfcFieldErrors(
  error: unknown,
  form: UseFormReturn<any>,
  fieldPrefix: 'section125Form' | 'mecForm' | null = null,
): void {
  for (const issue of extractIssues(error)) {
    const name = toRhfFieldName(issue.path ?? [], fieldPrefix);
    if (!name) continue;
    form.setError(name as never, {type: 'server', message: issue.message});
  }
}

export function rfcStepFieldPrefix(step: string): 'section125Form' | 'mecForm' | null {
  if (step.startsWith('SECTION_125_FORM')) return 'section125Form';
  if (step.startsWith('MEC_FORM')) return 'mecForm';
  return null;
}
